import { Box } from '@chakra-ui/react';
import { TabMenu, type TabItem } from '@themes/aegix';
import { useTranslation } from 'react-i18next';
import { UsersContent } from './UsersContent';
import { RolesContent } from './RolesContent';
import { SsoConfig } from './SsoConfig';
import { InvitationTemplatesContent } from './InvitationTemplatesContent';
import {
  ContactsContent,
  ContactTagsContent,
  DirectorySourcesContent,
} from './contacts';
import { useSearchParams } from 'react-router-dom';
import {
  useSsoCapabilities,
  hasSsoAdminAccess,
} from '../../hooks/useSsoCapabilities';
import { useFeature } from '../../hooks/useFeature';
import { FeatureCode } from '../../types/features';
import { useHasPermissions } from '../../utils/hasPermission';

/**
 * AuthorizationContent — body of the Account → People tab.
 *
 * There is no Organizations sub-tab (ADR-114 Phase 1): multi-tenant org
 * CRUD lives behind `VendorOrgSelector`'s Manage entry, and
 * `/account?tab=organizations` redirects to People.
 *
 * ADR-141 §4: Contacts + Tags sub-tabs. Both gate on the
 * MASS_NOTIFICATION_CONTACTS feature (flag + Module license, computed
 * server-side) AND the corresponding `contacts.view_*` permission; a
 * deep link to a hidden sub-tab falls back to `users`, matching the SSO
 * sub-tab's redirect-on-disabled pattern.
 *
 * ADR-141 §8 / P7: Directory Sources sub-tab — same feature
 * gate plus `contacts.manage_contactdirectorysource` (the viewset denies
 * the whole collection without it, so there is no read-only variant).
 */
export function AuthorizationContent({
  initialSubTab,
}: {
  initialSubTab?: string;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoCaps = useSsoCapabilities();
  const canAccessSso = hasSsoAdminAccess(ssoCaps.data);
  const contactsFeatureEnabled = useFeature(
    FeatureCode.MASS_NOTIFICATION_CONTACTS,
  );
  const canViewContacts = useHasPermissions(['contacts.view_contact']);
  const canViewTags = useHasPermissions(['contacts.view_contacttag']);
  const canManageSources = useHasPermissions([
    'contacts.manage_contactdirectorysource',
  ]);
  const showContactsTab = contactsFeatureEnabled && canViewContacts;
  const showTagsTab = contactsFeatureEnabled && canViewTags;
  const showSourcesTab = contactsFeatureEnabled && canManageSources;

  const authTabs: TabItem[] = [
    {
      value: 'users',
      label: t('account.tabUsers', 'Users'),
      'data-e2e': 'tab-users',
    },
    {
      value: 'roles',
      label: t('account.subtabs.roles', 'Roles'),
      'data-e2e': 'tab-roles',
    },
    ...(showContactsTab
      ? [
          {
            value: 'contacts',
            label: t('account.subtabs.contacts', 'Contacts'),
            'data-e2e': 'contactsSubtab',
          },
        ]
      : []),
    ...(showTagsTab
      ? [
          {
            value: 'tags',
            label: t('account.subtabs.tags', 'Tags'),
            'data-e2e': 'tagsSubtab',
          },
        ]
      : []),
    ...(showSourcesTab
      ? [
          {
            value: 'sources',
            label: t('account.subtabs.sources', 'Directory Sources'),
            'data-e2e': 'sourcesSubtab',
          },
        ]
      : []),
    {
      value: 'invitation-templates',
      label: t('account.subtabs.invitationTemplates', 'Invitation Templates'),
      'data-e2e': 'tab-invitation-templates',
    },
    ...(canAccessSso
      ? [
          {
            value: 'sso',
            label: t('account.subtabs.sso', 'SSO'),
            'data-e2e': 'tab-sso',
          },
        ]
      : []),
  ];

  const requestedTab =
    searchParams.get('subtab')?.toLowerCase().replace(/\s+/g, '-') ||
    initialSubTab?.toLowerCase().replace(/\s+/g, '-') ||
    'users';
  let activeTab = requestedTab;
  if (activeTab === 'sso' && !canAccessSso) activeTab = 'users';
  if (activeTab === 'contacts' && !showContactsTab) activeTab = 'users';
  if (activeTab === 'tags' && !showTagsTab) activeTab = 'users';
  if (activeTab === 'sources' && !showSourcesTab) activeTab = 'users';
  // Phase 1: Organizations sub-tab is gone. Redirect any legacy deep link.
  if (activeTab === 'organizations') activeTab = 'users';

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subtab', tab);
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };
  return (
    <Box>
      <TabMenu
        tabs={authTabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />
      {activeTab === 'users' && <UsersContent />}
      {activeTab === 'roles' && <RolesContent />}
      {activeTab === 'contacts' && showContactsTab && <ContactsContent />}
      {activeTab === 'tags' && showTagsTab && <ContactTagsContent />}
      {activeTab === 'sources' && showSourcesTab && <DirectorySourcesContent />}
      {activeTab === 'invitation-templates' && <InvitationTemplatesContent />}
      {activeTab === 'sso' && canAccessSso && <SsoConfig />}
    </Box>
  );
}
